import React from 'react';

import { Link } from '@/components/common';
import QuoteSection from '@/components/sections/quote-section';

type AboutHeader = {
    title: string;
    subTitle: string;
    description: string | React.ReactElement;
    imageUrl?: string;
};

type ProductStat = {
    title: string;
    number: number;
};

type AboutProductStats = {
    title: string;
    description: string | React.ReactElement;
    stats: ProductStat[];
};

interface SearchPageLayoutProps {
    header?: AboutHeader;
    productStats?: AboutProductStats;
}

// eslint-disable-next-line unused-imports/no-unused-vars
const AboutPageLayout = (props: SearchPageLayoutProps): JSX.Element => {
    const { header } = props;

    return (
        <div className="flex flex-col">
            <section className="px-5 md:px-10 lg:px-20 xl:px-40 py-24 xl:py-[200px]">
                <div className="flex flex-col xl:flex-row justify-center items-center relative gap-10 mx-auto max-w-xl">
                    <div className="flex flex-col relative gap-5 lg:justify-center xl:flex-grow-0 xl:flex-shrink-0 xl:w-full ">
                        {header?.imageUrl ? (
                            <picture>
                                <img src={header?.imageUrl} className="object-none" alt="about" />
                            </picture>
                        ) : (
                            <picture>
                                <img
                                    src="/images/about/AboutLaptop_v2.png"
                                    className="w-full max-w-sm lg:max-w-full object-cover"
                                    alt="about"
                                />
                            </picture>
                        )}
                    </div>
                    <div className="flex flex-col justify-center items-start flex-grow-0 flex-shrink-0 w-full relative gap-5">
                        <div className="text-center xl:text-left flex flex-col items-center lg:items-start gap-3">
                            <p className="text-sm text-left uppercase text-[#5637cd]">
                                {header?.title || 'about blockworks'}
                            </p>
                            <p className="text-4xl xl:text-5xl font-semibold text-left text-[#110b29] text-center lg:text-left">
                                {header?.subTitle || "We're building the backbone of crypto information."}
                            </p>
                        </div>
                        <div className="flex-grow-0 flex-shrink-0 text-lg xl:text-xl text-left text-[#110b29] text-center lg:text-left">
                            <>
                                {header?.description ||
                                    'Blockworks is a media and information platform that delivers news and insights about crypto to millions of investors. Our editorial site, newsletters, podcasts, conferences, research, and data provide investors with the critical analysis and information they need to make smarter decisions about digital assets.'}
                            </>
                        </div>
                    </div>
                </div>
            </section>
            <QuoteSection
                className="px-5 sm:px-12 md:px-24 xl:px-48 pb-24 xl:pb-[200px]"
                title="Blockworks is on a mission to make crypto more transparent."
                label="mission"
            >
                <div className="flex flex-col gap-2 text-md lg:text-xl text-left text-[#110b29]">
                    <p>
                        We curate meaningful and actionable facts, data, and truths. We transform that knowledge into
                        understanding by combining that foundation with thoughtful and expertise-driven insights and
                        context. We deliver these insights directly to our audience, empowering them to make informed
                        and timely decisions with clarity and confidence.
                    </p>
                </div>
            </QuoteSection>
            <QuoteSection
                className="px-5 sm:px-12 md:px-24 xl:px-48 pb-24 xl:pb-[200px]"
                title="Build the definitive global information platform for crypto."
                label="Vision"
            >
                <div className="flex flex-col gap-2 text-md lg:text-xl text-left text-[#110b29]">
                    <p>
                        We envision a world where anyone, anywhere can visit Blockworks and access every asset onchain
                        in real time, make sense of signal with insights, feel connected and informed through trusted
                        media and world-class events, and take confident action through intuitive tooling.
                    </p>
                </div>
            </QuoteSection>
            <section className="flex flex-col lg:flex-row justify-center items-center self-stretch gap-10 bg-[color:#f7f7f7] py-10 lg:py-20 xl:py-40 px-10 md:px-20 lg:px-20 xl:px-40">
                <div className="flex flex-col justify-between items-center relative">
                    <div className="flex flex-col justify-start items-start gap-4 max-w-lg">
                        <div className="flex flex-col justify-start items-start gap-3">
                            <p className="text-sm text-left uppercase text-[#5637cd]">careers</p>
                            <p className="text-[32px] text-left text-[#110b29]">Build with us.</p>
                        </div>
                        <div className="text-xl text-left text-[#110b29] flex flex-col gap-6">
                            <p>
                                We’re passionate about what we do, and are looking for creative, curious, hard-working
                                individuals who want to make an impact.
                            </p>
                            <p>
                                We value a flat organization, clear communication, and taking ownership over your goals.
                            </p>
                            <p>Come join our growing team!</p>
                        </div>
                        <p className="text-base text-left text-[#5637cd]">
                            <Link href="/careers" className="hover:text-brand">
                                View open positions
                            </Link>
                        </p>
                    </div>
                </div>
                <picture className=" h-[405.59px] relative">
                    <img
                        src="/images/about/BlockworksTeam.png"
                        srcSet="/images/about/BlockworksTeam.png 1x, /images/about/BlockworksTeam@2x.png 2x, /images/about/BlockworksTeam@3x.png 3x"
                        className="h-[405.59px] object-cover"
                        alt="blockworks-team"
                    />
                </picture>
            </section>
        </div>
    );
};

export default AboutPageLayout;
